<template>
  <div class="auth-container">
    <div class="auth-box">
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
      <h2>Login</h2>
      <form @submit.prevent="handleLogin">
        <div class="form-group">
          <input
            ref="usernameInput"
            type="text"
            v-model="username"
            placeholder="Username"
            required
            autocomplete="username"
          />
        </div>
        <div class="form-group">
          <input
            type="password"
            v-model="password"
            placeholder="Password"
            required
            autocomplete="current-password"
          />
        </div>
        <div class="demo-info">
          <p>This is a demo site, use password123 at all times</p>
        </div>
        <button type="submit" class="auth-button" :disabled="isLoggingIn">
          {{ isLoggingIn ? "Logging in..." : "Login" }}
        </button>
      </form>

      <div class="auth-links">
        <p>
          Don't have an account?
          <router-link to="/register">Create one</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { getVivamoCustomer } from "@/api/vivamo-api";

export default {
  data() {
    return {
      username: "",
      password: "",
      isLoggingIn: false,
      errorMessage: "",
      demoPassword: "password123",
    };
  },
  mounted() {
    this.$store.dispatch("logout");

    this.$nextTick(() => {
      this.$refs.usernameInput.focus();
    });
  },
  methods: {
    async handleLogin() {
      this.errorMessage = "";

      if (this.password !== this.demoPassword) {
        this.errorMessage = "Invalid password";
        return;
      }

      if (!this.username.trim()) {
        this.errorMessage = "Username is required";
        return;
      }

      this.isLoggingIn = true;

      try {
        const vivamoCustomer = await getVivamoCustomer(this.username.trim());

        if (!vivamoCustomer) {
          this.errorMessage =
            "Username not found. Please check your username or create an account.";
          return;
        }

        const userData = {
          username: this.username.trim(),
          email: vivamoCustomer.data?.email || "",
          vivamoCustomerId: vivamoCustomer.id || vivamoCustomer._id,
          loginAt: new Date().toISOString(),
        };

        localStorage.setItem("demoUserData", JSON.stringify(userData));

        this.$store.dispatch("login");
        this.$emit("login-success");
        this.$router.push("/games");
      } catch (error) {
        console.error("Login error:", error);
        this.errorMessage = "Login failed. Please try again later.";
      } finally {
        this.isLoggingIn = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.auth-box {
  background: var(--card-background);
  padding: 2rem;
  border-radius: 15px;
  width: 100%;
  max-width: 400px;
  border: 1px solid var(--accent-color);
}

.form-group {
  margin-bottom: 1rem;
}

input {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  background-color: var(--input-background);
  color: var(--text-color);
  box-sizing: border-box;
}

input::placeholder {
  color: var(--input-placeholder);
}

.auth-button {
  width: 100%;
  padding: 1rem;
  background-color: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.auth-button:hover:not(:disabled) {
  background-color: var(--accent-color);
  color: var(--primary-color);
}

.auth-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-links {
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--input-border);
}

.auth-links p {
  color: var(--text-color);
  margin: 0;
}

.auth-links a {
  color: var(--accent-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.auth-links a:hover {
  color: var(--secondary-color);
  text-decoration: underline;
}

.demo-info {
  text-align: center;
  margin-bottom: 1rem;
}

.demo-info p {
  color: var(--accent-color);
  font-size: 0.9rem;
  font-style: italic;
  margin: 0;
  padding: 0.5rem;
  background: rgba(218, 164, 93, 0.1);
  border-radius: 8px;
  border-left: 4px solid var(--accent-color);
}

.error-message {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  margin-top: 1rem;
}

.success-message {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.3);
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  margin-bottom: 1rem;
}

.warning-message {
  background: rgba(251, 191, 36, 0.1);
  color: #f59e0b;
  border: 1px solid rgba(251, 191, 36, 0.3);
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  margin-bottom: 1rem;
}

.success-message p,
.warning-message p {
  margin: 0;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .auth-container {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .auth-box {
    padding: 1.5rem;
    margin: 0;
    width: 100%;
    max-width: none;
    border-radius: 12px;
  }

  .auth-box h2 {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }

  .form-group {
    margin-bottom: 1.2rem;
  }

  input {
    padding: 1rem;
    font-size: 1rem;
    border-radius: 10px;
  }

  .auth-button {
    padding: 1.2rem;
    font-size: 1rem;
    border-radius: 30px;
    margin-top: 0.5rem;
  }

  .demo-info p {
    font-size: 0.85rem;
    padding: 0.75rem;
  }

  .auth-links {
    margin-top: 1.2rem;
    padding-top: 1.2rem;
  }

  .auth-links p {
    font-size: 0.9rem;
  }

  .success-message,
  .warning-message {
    font-size: 0.85rem;
    padding: 0.75rem;
    margin-bottom: 1.2rem;
  }
}

@media (max-width: 480px) {
  .auth-container {
    padding: 0.5rem;
    padding-top: 1rem;
  }

  .auth-box {
    padding: 1.2rem;
    border-radius: 8px;
  }

  .auth-box h2 {
    font-size: 1.6rem;
  }

  input {
    padding: 0.9rem;
    font-size: 0.95rem;
  }

  .auth-button {
    padding: 1rem;
    font-size: 0.95rem;
  }

  .demo-info p {
    font-size: 0.8rem;
    padding: 0.6rem;
  }

  .error-message {
    font-size: 0.85rem;
    padding: 0.6rem;
  }

  .success-message,
  .warning-message {
    font-size: 0.8rem;
    padding: 0.6rem;
    margin-bottom: 1rem;
  }
}
</style>
